/**
 * Creating a machine.
 *
 * Everything an owner decides at the moment a machine is made: what it is, what it may spend, and what
 * it may touch. Limits are not optional extras here. A machine is created with them or not at all.
 */

import { z } from "zod";

const address = z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/, "not an address");
const whole = z.string().regex(/^\d+$/, "not a whole number");
/** A whole number that may be negative, because a result can be a loss. */
const signedWhole = z.string().regex(/^-?\d+$/, "not a whole number");

export const CreateMachineRequest = z
	.strictObject({
		/** Who it belongs to. Taken from the session by the gateway, never from a caller. */
		ownerId: z.uuid(),
		/**
		 * The owner's own wallet: the only address a machine's funds can ever reach, which the wallet
		 * provider's policy pins payouts to. Required for a machine that holds real money. A paper
		 * machine has no funds and no policy, so somebody who has never linked one can still make one.
		 */
		ownerWallet: address.optional(),
		name: z.string().min(1).max(60),
		kind: z.string().min(1).max(40),
		/**
		 * Whether this machine only pretends to trade.
		 *
		 * Said out loud, every time, because it is the difference between pretending and spending and
		 * it is decided once: a machine never changes between the two. Optional used to mean live,
		 * which is the wrong way round. A mistake here should cost nothing, not everything.
		 */
		paper: z.boolean(),
		settings: z.record(z.string(), z.unknown()),
		rules: z.record(z.string(), z.unknown()).optional(),
		limits: z.strictObject({
			budgetGranted: whole,
			maxPerTrade: whole.optional(),
			maxPerDay: whole.optional(),
			approvedMints: z.array(address).min(1).max(20),
		}),
	})
	.meta({ id: "CreateMachineRequest" });
export type CreateMachineRequest = z.infer<typeof CreateMachineRequest>;

export const CreateMachineResponse = z
	.strictObject({
		machineId: z.string(),
		ownerId: z.string(),
		/** The machine's own wallet, which the owner funds. */
		walletAddress: address,
		definitionId: z.string(),
	})
	.meta({ id: "CreateMachineResponse" });
export type CreateMachineResponse = z.infer<typeof CreateMachineResponse>;

/** A machine as an owner sees it in a list. */
export const MachineSummary = z
	.strictObject({
		machineId: z.string(),
		name: z.string(),
		kind: z.string(),
		walletAddress: address,
		createdAt: z.iso.datetime(),
		/** On paper: it trades against real prices, moves no money, and has nothing to fund. */
		paper: z.boolean(),
		state: z.enum(["draft", "ready", "running", "paused", "stopped"]),
		stateReason: z.string().optional(),
		budget: z.strictObject({
			granted: whole,
			reserved: whole,
			settled: whole,
			available: whole,
		}),
		/**
		 * What the machine has actually made, which the budget never said.
		 *
		 * Signed, because a machine can be down. Only closed trades count: an open position is reported
		 * as what it cost, not as profit, because until it is sold that is an opinion.
		 *
		 * Fees are their own number and in lamports, while the result is in whatever the machine spends.
		 * Adding them needs a price, and a single number that quietly assumes one is worse than two that
		 * do not.
		 */
		result: z.strictObject({
			realised: signedWhole,
			position: whole,
			basis: whole,
			feesLamports: whole,
			trades: z.int().nonnegative(),
			roundTrips: z.int().nonnegative(),
			wins: z.int().nonnegative(),
			losses: z.int().nonnegative(),
			/** True when any of it came from a machine on paper, so the numbers are not money. */
			simulated: z.boolean(),
		}),
	})
	.meta({ id: "MachineSummary" });
export type MachineSummary = z.infer<typeof MachineSummary>;

export const MachineList = z
	.strictObject({ machines: z.array(MachineSummary) })
	.meta({ id: "MachineList" });
export type MachineList = z.infer<typeof MachineList>;

/** One machine, with what it is set to do and what its owner may do next. */
export const MachineDetail = MachineSummary.extend({
	settings: z.unknown(),
	limits: z.strictObject({
		maxPerTrade: whole.optional(),
		maxPerDay: whole.optional(),
		approvedMints: z.array(address),
	}),
	actions: z.array(z.enum(["fund", "start", "pause", "resume", "stop"])),
	/** The prices it is waiting on now, in micro-dollars, worked out from its whole record. */
	levels: z.array(
		z.strictObject({
			id: z.string(),
			price: whole,
			direction: z.enum(["falls_to", "rises_to"]),
		}),
	),
}).meta({ id: "MachineDetail" });
export type MachineDetail = z.infer<typeof MachineDetail>;

/** What one account holds, read from the chain: SOL in lamports and each token in its smallest unit. */
const Holdings = z.strictObject({
	address,
	lamports: whole,
	tokens: z.array(
		z.strictObject({ mint: address, amount: whole, decimals: z.int().min(0).max(18) }),
	),
});

/** What a machine holds right now: its trading wallet, and its vault when it has one. */
export const MachineBalances = z
	.strictObject({ wallet: Holdings, vault: Holdings.optional() })
	.meta({ id: "MachineBalances" });
export type MachineBalances = z.infer<typeof MachineBalances>;

/** Money to send a machine from the owner's own wallet: dollars to trade with, and SOL for its fees. */
export const FundingRequest = z
	.strictObject({ usdc: whole, lamports: whole })
	.meta({ id: "FundingRequest" });
export type FundingRequest = z.infer<typeof FundingRequest>;

/** An unsigned transaction for the owner's wallet to approve, as base64. */
export const FundingTransaction = z
	.strictObject({ transaction: z.string().min(1), lastValidBlockHeight: whole })
	.meta({ id: "FundingTransaction" });
export type FundingTransaction = z.infer<typeof FundingTransaction>;

/** What an owner asks a machine to do. Funding carries the budget it may now spend in total. */
export const MachineActionRequest = z
	.strictObject({
		action: z.enum(["fund", "start", "pause", "resume", "stop"]),
		budgetGranted: whole.optional(),
	})
	.meta({ id: "MachineActionRequest" });
export type MachineActionRequest = z.infer<typeof MachineActionRequest>;

/** A new recipe for a paused machine: the kind it runs, and how that kind is set. */
export const RetuneMachineRequest = z
	.strictObject({
		kind: z.string().regex(/^[a-z][a-z0-9_]{2,39}$/),
		settings: z.record(z.string(), z.unknown()),
	})
	.meta({ id: "RetuneMachineRequest" });
export type RetuneMachineRequest = z.infer<typeof RetuneMachineRequest>;

export const RetuneMachineResponse = z
	.strictObject({ definitionId: z.string().regex(/^[0-9a-f]{64}$/) })
	.meta({ id: "RetuneMachineResponse" });
export type RetuneMachineResponse = z.infer<typeof RetuneMachineResponse>;

export const MachineActionResponse = z
	.strictObject({ state: z.enum(["draft", "ready", "running", "paused", "stopped"]) })
	.meta({ id: "MachineActionResponse" });
export type MachineActionResponse = z.infer<typeof MachineActionResponse>;

/** A machine's record: what it did, and why, newest first. */
export const MachineEvent = z
	.strictObject({
		id: z.string(),
		type: z.string(),
		occurredAt: z.iso.datetime(),
		payload: z.unknown(),
	})
	.meta({ id: "MachineEvent" });
export type MachineEvent = z.infer<typeof MachineEvent>;

export const MachineRecord = z
	.strictObject({ events: z.array(MachineEvent) })
	.meta({ id: "MachineRecord" });
export type MachineRecord = z.infer<typeof MachineRecord>;
